/** KMBS (potassium metabisulfite) yields ~57.6% SO₂ by weight. */
export const KMBS_SO2_YIELD = 0.576;

/** US gallons → liters. */
export const LITERS_PER_US_GALLON = 3.78541;

/** pKa of SO₂ used for Henderson–Hasselbalch Free SO₂ from molecular SO₂. */
export const SO2_PKA = 1.81;

export type VolumeUnit = 'liters' | 'gallons';

export type CalculatorMode = 'crush' | 'aging';

export type So2SourceId = 'kmbs' | 'liquid10' | 'liquid6';

export type AdditionUnit = 'g' | 'mL';

export type So2Source = {
  id: So2SourceId;
  label: string;
  yield: number;
  unit: AdditionUnit;
};

export const SO2_SOURCES: Record<So2SourceId, So2Source> = {
  kmbs: { id: 'kmbs', label: 'KMBS (powder)', yield: KMBS_SO2_YIELD, unit: 'g' },
  liquid10: { id: 'liquid10', label: '10% Liquid SO₂', yield: 0.1, unit: 'mL' },
  liquid6: { id: 'liquid6', label: '6% Liquid SO₂', yield: 0.06, unit: 'mL' },
};

/** Standard molecular SO₂ targets for aging. */
export const MOLECULAR_SO2_OPTIONS = [
  { value: 0.5, label: '0.5 — Reds' },
  { value: 0.8, label: '0.8 — Whites / sweets' },
] as const;

/** Convert volume input to liters for the formula. */
export function toLiters(volume: number, unit: VolumeUnit): number {
  if (!Number.isFinite(volume)) return NaN;
  return unit === 'gallons' ? volume * LITERS_PER_US_GALLON : volume;
}

/**
 * Target Free SO₂ (ppm) from molecular SO₂ and pH:
 * T = M × (1 + 10^(pH − 1.81))
 */
export function targetFreeSo2FromMolecular(molecularSo2: number, pH: number): number | null {
  if (!Number.isFinite(molecularSo2) || !Number.isFinite(pH) || molecularSo2 <= 0) {
    return null;
  }
  return molecularSo2 * (1 + 10 ** (pH - SO2_PKA));
}

export type So2AdditionResult = {
  amount: number | null;
  unit: AdditionUnit;
  volumeLiters: number | null;
  targetFreeSo2: number | null;
  noAdditionRequired: boolean;
};

/**
 * Grams or mL of SO₂ source to add:
 * amount = V × (T − C) / (1000 × Y)
 *
 * When C ≥ T and inputs are otherwise valid, returns amount 0 and noAdditionRequired.
 */
export function calculateSo2Addition(params: {
  volume: number;
  volumeUnit: VolumeUnit;
  currentFreeSo2: number;
  targetFreeSo2: number;
  source: So2SourceId;
}): So2AdditionResult {
  const source = SO2_SOURCES[params.source];
  const volumeLiters = toLiters(params.volume, params.volumeUnit);
  const { currentFreeSo2, targetFreeSo2 } = params;

  if (
    !Number.isFinite(volumeLiters) ||
    !Number.isFinite(currentFreeSo2) ||
    !Number.isFinite(targetFreeSo2) ||
    volumeLiters <= 0 ||
    currentFreeSo2 < 0 ||
    targetFreeSo2 < 0
  ) {
    return {
      amount: null,
      unit: source.unit,
      volumeLiters: null,
      targetFreeSo2: Number.isFinite(targetFreeSo2) ? targetFreeSo2 : null,
      noAdditionRequired: false,
    };
  }

  if (currentFreeSo2 >= targetFreeSo2) {
    return {
      amount: 0,
      unit: source.unit,
      volumeLiters,
      targetFreeSo2,
      noAdditionRequired: true,
    };
  }

  const amount =
    (volumeLiters * (targetFreeSo2 - currentFreeSo2)) / (1000 * source.yield);

  return {
    amount,
    unit: source.unit,
    volumeLiters,
    targetFreeSo2,
    noAdditionRequired: false,
  };
}

/** Crush / pre-ferment: user supplies Target Free SO₂ directly. */
export function calculateCrushAddition(params: {
  volume: number;
  volumeUnit: VolumeUnit;
  currentFreeSo2: number;
  targetFreeSo2: number;
  source: So2SourceId;
}): So2AdditionResult {
  return calculateSo2Addition(params);
}

/** Aging: derive Target Free SO₂ from molecular SO₂ and pH, then add. */
export function calculateAgingAddition(params: {
  volume: number;
  volumeUnit: VolumeUnit;
  currentFreeSo2: number;
  pH: number;
  molecularSo2: number;
  source: So2SourceId;
}): So2AdditionResult {
  const targetFreeSo2 = targetFreeSo2FromMolecular(params.molecularSo2, params.pH);
  if (targetFreeSo2 === null) {
    return {
      amount: null,
      unit: SO2_SOURCES[params.source].unit,
      volumeLiters: null,
      targetFreeSo2: null,
      noAdditionRequired: false,
    };
  }
  return calculateSo2Addition({
    volume: params.volume,
    volumeUnit: params.volumeUnit,
    currentFreeSo2: params.currentFreeSo2,
    targetFreeSo2,
    source: params.source,
  });
}

/** Format addition amount for display (2 decimal places), or em dash when null. */
export function formatSo2Amount(amount: number | null): string {
  if (amount === null || !Number.isFinite(amount)) return '—';
  return amount.toFixed(2);
}
